import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";
import CommercialGate from "../../components/commercial/CommercialGate";
import StatusBadge from "../../components/StatusBadge";
import { api } from "../../lib/api";
import { fmtCents, setPageTitle } from "../../lib/format";

const STATUSES = ["", "pending", "pending_auth", "authorized", "paid", "cancelled", "expired", "refunded"];

// Commercial operator booking console (V2 P2) — strictly owner-scoped server-side.
function BookingsBody() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [propertyId, setPropertyId] = useState("");
  const [props_, setProps] = useState([]);
  const [page, setPage] = useState(1);

  useEffect(() => { api.commercialProperties().then((r) => setProps(r.properties)).catch(() => {}); }, []);

  const load = useCallback(() => {
    const p = { page };
    if (status) p.status = status;
    if (propertyId) p.property_id = propertyId;
    api.commercialBookings(p).then(setData).catch((e) => setError(e.message));
  }, [status, propertyId, page]);
  useEffect(() => { setPageTitle("Commercial Bookings"); load(); }, [load]);

  return (
    <>
      <div className="flex flex-wrap gap-2 items-center" data-testid="commercial-booking-filters">
        <select value={propertyId} onChange={(e) => { setPropertyId(e.target.value); setPage(1); }}
                className="rounded-xl border border-[#27272A] bg-[#0A0D10] px-3 py-2 text-xs text-white" data-testid="filter-property" aria-label="Filter by property">
          <option value="">All properties</option>
          {props_.map((p) => <option key={p.property_id} value={p.property_id}>{p.name} ({p.property_code})</option>)}
        </select>
        <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}
                className="rounded-xl border border-[#27272A] bg-[#0A0D10] px-3 py-2 text-xs text-white" data-testid="filter-status" aria-label="Filter by status">
          {STATUSES.map((s) => <option key={s} value={s}>{s ? s.replace("_", " ") : "All statuses"}</option>)}
        </select>
      </div>

      <div className="mt-5">
        {error && <div className="card-dark p-8 text-center text-sm text-[#FF6B6B]" role="alert">{error}</div>}
        {!error && !data && <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="card-dark h-20 animate-pulse" />)}</div>}
        {!error && data && data.items.length === 0 && (
          <div className="card-dark p-12 text-center" data-testid="bookings-empty">
            <p className="type-h3">No bookings yet</p>
            <p className="text-sm text-zinc-500 mt-2">Share your property code or QR sign to start receiving bookings.</p>
            <Link to="/commercial/properties" className="btn-secondary inline-flex mt-5 text-sm">Open properties</Link>
          </div>
        )}
        {!error && data && data.items.length > 0 && (
          <div className="space-y-3" data-testid="bookings-list">
            {data.items.map((b) => (
              <div key={b.booking_id} className="card-dark p-5 flex flex-wrap items-center gap-4" data-testid={`booking-row-${b.booking_id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-sm">{b.property_name || b.spot_title}</p>
                    <StatusBadge status={b.status} />
                  </div>
                  <p className="text-xs text-zinc-500 mt-1">
                    {b.category_name} · spot {b.property_spot_ref} ·{" "}
                    {new Date(b.start_time).toLocaleString()} → {new Date(b.end_time).toLocaleTimeString()}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-display font-bold">{fmtCents(b.total_cents, b.currency)}</p>
                  <p className="text-[10px] text-zinc-600 uppercase tracking-wider">{b.rate_plan_snapshot?.kind || "rate"}</p>
                </div>
              </div>
            ))}
            {data.pages > 1 && (
              <div className="flex items-center justify-between text-xs text-zinc-500 pt-2">
                <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="btn-secondary !py-1.5 !px-3 disabled:opacity-40" data-testid="bookings-prev">← Prev</button>
                <span>Page {data.page} of {data.pages}</span>
                <button disabled={page >= data.pages} onClick={() => setPage(page + 1)} className="btn-secondary !py-1.5 !px-3 disabled:opacity-40" data-testid="bookings-next">Next →</button>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

export default function CommercialBookings() {
  return (
    <CommercialGate>
      <div className="min-h-screen flex flex-col" data-testid="commercial-bookings">
        <Navbar />
        <main className="flex-1 max-w-5xl w-full mx-auto px-5 sm:px-8 pt-28 pb-16">
          <p className="section-label">Commercial</p>
          <h1 className="type-display mt-3">My Bookings</h1>
          <div className="mt-8"><BookingsBody /></div>
        </main>
        <Footer />
      </div>
    </CommercialGate>
  );
}
